import { useState } from "react";
import { pathsOverlap } from "../utils/path-overlap.js";
import { PathPickerModal } from "./path-picker-modal.js";

// 2.5.E.2: source-rootien muokkaus listana + Lisää/Poista-painikkeet.
//
// Trio-review R4: paths-prop sisältää konfigissa olevat polut.
// observedPaths-prop on daemonin raportoima sourceRootsObserved-lista
// (kts. /daemon/heartbeat). Vertailu näyttää varoituksen jos config-polku
// ei ole observed-listalla — todennäköisesti whitelist-hylkäys tai
// fs-tasolla puuttuu.

export type SourceRootsEditorProps = {
  paths: string[];
  observedPaths?: string[] | null;
  /** Kutsutaan kun lista muuttuu — kutsuja tallentaa serverille. */
  onChange: (next: string[]) => Promise<void> | void;
  disabled?: boolean;
  /** 3.A.7.4 — Daemonin machineId, mahdollistaa "Selaa Finderissa" -napin. */
  machineId?: string;
};

export function SourceRootsEditor({
  paths,
  observedPaths,
  onChange,
  disabled,
  machineId,
}: SourceRootsEditorProps) {
  const [adding, setAdding] = useState("");
  const [busy, setBusy] = useState(false);
  const [showPicker, setShowPicker] = useState(false);
  const [overlapError, setOverlapError] = useState<string | null>(null);
  const observed = new Set(observedPaths ?? []);
  const observedKnown = observedPaths !== undefined && observedPaths !== null;

  async function commit(next: string[]) {
    setBusy(true);
    try {
      await onChange(next);
    } finally {
      setBusy(false);
    }
  }

  async function handleAdd(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = adding.trim();
    if (trimmed.length === 0 || paths.includes(trimmed)) return;

    // Overlap-tarkistus: estä jos uusi polku on nykyisten ali- tai yli-polku
    const overlapping = paths.find((p) => pathsOverlap(p, trimmed));
    if (overlapping) {
      setOverlapError(
        `Polku "${trimmed}" on päällekkäinen olemassa olevan polun "${overlapping}" kanssa. Source-rootit eivät saa olla toistensa ali- tai yli-polkuja.`
      );
      return;
    }

    setOverlapError(null);
    setAdding("");
    await commit([...paths, trimmed]);
  }

  async function handleRemove(p: string) {
    await commit(paths.filter((x) => x !== p));
  }

  return (
    <div className="space-y-3">
      {paths.length === 0 ? (
        <p className="text-sm italic text-slate-500">Ei vielä source-rootteja.</p>
      ) : (
        <ul className="divide-y divide-slate-700">
          {paths.map((p) => {
            const isObserved = observed.has(p);
            return (
              <li
                key={p}
                className="flex items-center justify-between gap-2 py-2 text-sm"
              >
                <div className="flex-1 min-w-0">
                  <code className="block truncate text-slate-100">{p}</code>
                  {observedKnown && !isObserved && (
                    <p className="mt-0.5 text-xs text-yellow-400">
                      Daemon ei seuraa tätä polkua — tarkista polun
                      olemassaolo ja oikeudet. (Mahdollisesti hylätty
                      whitelist:lla: vain /Users, /Volumes, /home, /mnt
                      sallittu.)
                    </p>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => void handleRemove(p)}
                  disabled={disabled || busy}
                  className="rounded bg-red-900/40 px-2 py-1 text-xs font-medium text-red-200 hover:bg-red-900/60 disabled:opacity-50"
                >
                  Poista
                </button>
              </li>
            );
          })}
        </ul>
      )}
      <form onSubmit={handleAdd} className="flex gap-2">
        <input
          type="text"
          value={adding}
          onChange={(e) => {
            setAdding(e.target.value);
            if (overlapError) setOverlapError(null);
          }}
          placeholder="/Users/<sinun-nimi>/Pictures"
          disabled={disabled || busy}
          className="flex-1 rounded bg-slate-800 px-3 py-2 text-sm text-slate-100 placeholder-slate-500 disabled:opacity-50"
        />
        <button
          type="button"
          onClick={() => setShowPicker(true)}
          disabled={disabled || busy}
          className="rounded bg-slate-700 px-3 py-2 text-sm font-medium text-slate-100 hover:bg-slate-600 disabled:opacity-50"
          title="Selaa polku"
        >
          📁
        </button>
        <button
          type="submit"
          disabled={disabled || busy || adding.trim().length === 0}
          className="rounded bg-cyan-700 px-4 py-2 text-sm font-medium text-white hover:bg-cyan-600 disabled:opacity-50"
        >
          Lisää
        </button>
      </form>
      {overlapError && (
        <p className="text-xs text-red-400">{overlapError}</p>
      )}
      {showPicker && (
        <PathPickerModal
          title="Lisää source-root"
          initialPath={adding}
          {...(machineId ? { machineId } : {})}
          onSelect={(p) => setAdding(p)}
          onClose={() => setShowPicker(false)}
        />
      )}
    </div>
  );
}
